import { useFrame } from '@react-three/fiber';
import { FunctionComponent, memo, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Group, Vector3 } from 'three';
import { Jute } from '../..';
import { transitionManager } from '../../hooks';
import { useAllSceneAutoplayVideoIds, useAllSceneEntityIds, useGet360VideoIdBySceneId, useIsSceneFlatImageTracked, useModelIdleAnimationData, useSceneArEntityIds } from '../../hooks/general';
import { IEntityActionTypes, useActionStateStore } from '../../zustand';
import { useDataStore } from '../../zustand/data-store';
import { useSceneStore, ISceneActions, ITransCatTypes } from '../../zustand/scene-store';
import { IActionCategory, IComponentType, IPlaySoundAction, ISceneComp, ISpatialComponentUnion, ITransitionCategoryTypes, ITransitionData, ITransitionEffectTypes, ITuple3 } from '@r3f-component-data-structure';
import FadeGroup from '../FadeGroup';
import { useGetFaceTrackedComponentIds, useGetScreenRelativeComponentIds } from '@r3f/hooks/useGetScreenRelativeComponentIds';
import { getOpacityTransitionRange, getSpatialTransitionRange, IEasingTypes, Transition } from '@r3f/transitions';
import { ITransitionPhase } from '@r3f/transitions/spec';
import ArComponents from './ArComponents';
import { getAllOnloadModelAnimationPlayDataBySceneId } from '@r3f/utils/actions';
import { getAllAnimationControllerOptions } from '../../utils/action';
import { ITransitionSyncState, syncTransitionConfig, transitionSignature } from '../../utils/transitionSync';
import { presetAnimationController } from '../PresetAnimationController';
import { return360VideoIdBySceneId } from '@r3f/utils/content360';
import { getAllModelIdsBySceneId } from '@r3f/utils/modelAnimations';

interface IParentProps {
	id: string;
	visible?: boolean;
}

const Scene: FunctionComponent<IParentProps> = ({ id: sceneId, visible = true }) => {
	const fadeGroupRef = useRef<Group>(null);
	const previousSceneId = useSceneStore((state) => state.previousSceneId);
	// const { scene: threeScene } = useThree();
	
	// create transition objects on mount
	const isTestMode = Jute.environment === 'test';
	const sceneSpatialTransition = useMemo(
		() => new Transition<ITuple3>({ start: [0, 0, 0], end: [0, 0, 0], duration: 0, easingType: IEasingTypes.linear, delay: 0, isTestMode, transitionId: `spatial-${sceneId}` }),
		[]
	);
	const sceneOpacityTransition = useMemo(() => new Transition<number>({ start: 0, end: 0, duration: 0, easingType: IEasingTypes.linear, delay: 0, isTestMode, transitionId: `opacity-${sceneId}` }), []);

	useFrame(({ camera }) => {
		camera.updateMatrixWorld();
		sceneSpatialTransition.tick();
		sceneOpacityTransition.tick();
	});

	const memoZeroTuple3 = useMemo(() => [0, 0, 0], []);

	// The data store DOES update at runtime — `Jute.applyContentUpdate` re-dispatches componentsById on
	// every live edit from Designer. Any hook deriving from content must therefore depend on the content
	// reference (see PROJECT_JUTE_PREVIEW.md in the Designer repo); memoizing on `[]` freezes it at mount.
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	const rootId = useDataStore(({ rootId }) => rootId);
	const scene = useMemo(() => componentsById?.[sceneId], [sceneId, componentsById]) as ISceneComp;
	const screenRelativeIds = useGetScreenRelativeComponentIds(sceneId, componentsById);
	const faceTrackedIds = useGetFaceTrackedComponentIds(sceneId, componentsById);
	const contentIds = useMemo(() => [...scene.children, ...screenRelativeIds, ...faceTrackedIds], [scene.children, screenRelativeIds, faceTrackedIds]);
	const [sceneDispatch] = useState(() => useSceneStore.getState().dispatch);
	const experienceIsMuted = useActionStateStore(({ experienceIsMuted }) => experienceIsMuted);
	const [entityActionDispatch] = useState(() => useActionStateStore.getState().dispatch);
	const placementMode = useSceneStore(({ placementMode }) => placementMode);
	const parentSceneIsFlatImageTracked = useIsSceneFlatImageTracked(scene);

	const sceneOnLoadPlaySoundActions = useMemo(() => scene?.actions?.onLoad?.filter(action => action.type === IActionCategory.playSound), [scene]) as IPlaySoundAction[];

	const lastSceneOpacityConfig = useRef<ITransitionSyncState>({});
	const lastSceneSpatialConfig = useRef<ITransitionSyncState>({});

	/**
	 * Everything the scene-level transitions are built from.
	 *
	 * The children are in here for two reasons. Both scene transitions stretch their duration to cover the
	 * longest child transition, so a child's `delay`/`duration` is a scene-level input. And a child's
	 * transform decides where it lands, so an entity moved while the scene is transitioning has to count as
	 * a change here too — otherwise the scene keeps animating towards the old layout. See
	 * PROJECT_JUTE_PREVIEW.md.
	 *
	 * The point of a signature rather than a `componentsById` dependency is that content updates arrive for
	 * every edit, including ones these transitions cannot see — recolouring a button, retyping a label. The
	 * effects below re-run for those; the signature is what stops them acting on them.
	 */
	const sceneConfigSignature = useMemo(() => {
		const parts: unknown[] = [scene?.transitions];
		for (let i = 0; i < contentIds.length; i++) {
			const child = componentsById?.[contentIds[i]] as ISpatialComponentUnion | undefined;
			if (!child) continue;
			parts.push(contentIds[i], child.position, child.scale, child.rotation, child.transitions);
		}
		return transitionSignature(...parts);
	}, [scene?.transitions, contentIds, componentsById]);

	// Re-rendering useful to intialise spring prop changes
	const transitionCategory = useSceneStore((state) => state.transitionCategory[sceneId]);

	const sceneModelIds = useSceneArEntityIds(sceneId, IComponentType.Model3d);
	const sceneVideoIds = useAllSceneEntityIds(sceneId, IComponentType.Video);
	const sceneModelIdleAnimationData = useModelIdleAnimationData(sceneId);
	const visibleAutoplayVideoIds = useAllSceneAutoplayVideoIds(sceneId, true);

	const scene360VideoId = useGet360VideoIdBySceneId(sceneId);

	if (scene360VideoId) visibleAutoplayVideoIds.push(scene360VideoId);

	useLayoutEffect(() => {
		if (!sceneId || !componentsById) return;
		entityActionDispatch?.({
			type: IEntityActionTypes.SETUP_ALL_SCENE_AUDIO_BY_SCENE_ID,
			payload: { id: sceneId, componentsById },
		});
	}, [sceneId, componentsById, entityActionDispatch]);

	// REFACTOR: MOVE THIS TO A NEW HIGHER SCENE CONTENT component (parent of sceneSrContent and sceneArContent)

	// Play/pause media and animations depending on scene state changes.
	useLayoutEffect(() => {
		// Pause all video, animations and sound if in placement mode or scene hidden.
		if (placementMode || transitionCategory === 'hidden') {
			if (sceneModelIds?.length) {
				entityActionDispatch({
					type: IEntityActionTypes.PAUSE_MODEL_ANIMATIONS_BY_ID,
					payload: sceneModelIds,
				});
			}
			if (sceneVideoIds?.length && !!previousSceneId) {
				entityActionDispatch({
					type: IEntityActionTypes.PAUSE_VIDEOS_BY_ID,
					payload: { ids: sceneVideoIds },
				});
			}
			entityActionDispatch?.({
				type: IEntityActionTypes.PAUSE_ALL_SCENE_AUDIO_BY_SCENE_ID,
				payload: { id: sceneId, resetToStart: true },
			});

			entityActionDispatch?.({
				type: IEntityActionTypes.PAUSE_VIDEOS_BY_ID,
				payload: { ids: [return360VideoIdBySceneId(sceneId)] },
			})
			return;
		}
		if (transitionCategory === ITransCatTypes.enter) {
			if (visibleAutoplayVideoIds?.length) {
				entityActionDispatch({
					type: IEntityActionTypes.PLAY_VIDEOS_BY_ID,
					payload: { ids: visibleAutoplayVideoIds },
				});
			}
		}
	}, [transitionCategory, entityActionDispatch, visibleAutoplayVideoIds, placementMode, sceneModelIds, sceneVideoIds, sceneId]);

	const enterOrExit = useMemo(() => {
		return transitionCategory === ITransCatTypes.enter ? ITransitionCategoryTypes.enter : ITransitionCategoryTypes.exit;
	}, [transitionCategory]);

	// Register the scene's own spatial transition with its group, and unregister it again on unmount.
	//
	// The module-level `transitionAddedDict[sceneId]` guard this replaces outlived the component. A scene
	// deleted and re-added during a preview session came back with a fresh `Transition` that the guard
	// refused to register, while the dead one stayed in the group forever — and since the group's callbacks
	// only fire once every member has reported, that one orphan stopped `onStartAll` / `onFinishAll` for the
	// scene permanently. `add` de-duplicates by identity, so no guard is needed. Mirrors EntityLevelFadeGroup.
	useLayoutEffect(() => {
		transitionManager[sceneId].add(sceneSpatialTransition);
		return () => {
			transitionManager[sceneId]?.remove(sceneSpatialTransition);
		};
	}, [sceneId, sceneSpatialTransition]);

	// set transition phases for scene depending on transition events (onFinish, onStart...) & categories
	useLayoutEffect(() => {
		if (transitionCategory === ITransCatTypes.hidden) return;

		// console.log('set transition phase by category', sceneId, transitionCategory)
		transitionManager[sceneId].onStartAll(() => {
			// console.log('onStartAll in scene logic');
			// on scene enter start
			
			if (transitionCategory === ITransCatTypes.enter) {
				console.log('SCENE: start enter transition', sceneId);
				sceneDispatch?.({
					type: ISceneActions.SET_TRANSITION_PHASE,
					transPhaseDict: { [sceneId]: ITransitionPhase.enterStart },
				});
				
				presetAnimationController
					.set({options: getAllAnimationControllerOptions(componentsById!).preset})
					.startAllOnloadAnimationsBySceneId(sceneId)
				
				entityActionDispatch?.({
					type: IEntityActionTypes.PLAY_MODEL_ANIMATIONS_BY_ID,
					payload: getAllOnloadModelAnimationPlayDataBySceneId(componentsById, sceneId)
				});
			
				sceneOnLoadPlaySoundActions?.forEach((action) => {
					let { stopMedia, loop } = action;
					if (stopMedia) {
						entityActionDispatch?.({
							type: IEntityActionTypes.PAUSE_ALL_VIDEOS,
						});
						entityActionDispatch?.({
							type: IEntityActionTypes.PAUSE_ALL_SCENE_AUDIO_BY_SCENE_ID,
							payload: { id: sceneId },
						});
						entityActionDispatch?.({
							type: IEntityActionTypes.PAUSE_BACKGROUND_AUDIO,
						});
					}
					entityActionDispatch?.({
						type: IEntityActionTypes.PLAY_ALL_SCENE_AUDIO_BY_SCENE_AND_ENTITY_ID,
						payload: { sceneId, entityIds: [sceneId] },
					});
				})
				return;
			}
			// on scene exit Start
			console.log('SCENE: start exit transition', sceneId);
			sceneDispatch?.({
				type: ISceneActions.SET_TRANSITION_PHASE,
				transPhaseDict: { [sceneId]: ITransitionPhase.exitStart },
			});
		});
		transitionManager[sceneId].onFinishAll(() => {
			// console.log('onFinishAll in scene logic');
			// on scene enter finish
			if (transitionCategory === ITransCatTypes.enter) {
				console.log('SCENE: enter finish', sceneId);
				sceneDispatch?.({
					type: ISceneActions.SET_TRANSITION_PHASE,
					transPhaseDict: { [sceneId]: ITransitionPhase.enterFinish },
				});
				// Start play idle animations on scene transition enter finish.
				// if (sceneModelIdleAnimationData?.length) {
				// 	entityActionDispatch?.({
				// 		type: IEntityActionTypes.PLAY_MODEL_ANIMATIONS_BY_ID,
				// 		payload: sceneModelIdleAnimationData,
				// 	});
				// }

				return;
			}
			console.log('SCENE: exit finish', sceneId);
			// on scene exit finish pause media
			sceneDispatch?.({
				type: ISceneActions.SET_TRANSITION_CATEGORY,
				transCatDict: { [sceneId]: ITransCatTypes.hidden },
			});
			sceneDispatch?.({
				type: ISceneActions.SET_TRANSITION_PHASE,
				transPhaseDict: { [sceneId]: ITransitionPhase.exitFinish },
			});

			// Reset/pause all scene animations 
			presetAnimationController.resetAllAnimationsBySceneId(sceneId);
			if (!componentsById) return;
			entityActionDispatch?.({
				type: IEntityActionTypes.PAUSE_MODEL_ANIMATIONS_BY_ID,
				payload: getAllModelIdsBySceneId(sceneId, componentsById)
			})
		});
		// `componentsById` is read throughout the body above but was NOT listed here. That was a real
		// staleness bug hiding behind an accident: `sceneModelIdleAnimationData` comes from
		// `useModelIdleAnimationData`, which returns a freshly-mapped array on every call, so that dep
		// changed every render and dragged this effect along with it. Memoising that hook — an obvious and
		// tempting perf cleanup — would have silently converted this into stale onLoad animations and
		// sounds. Depending on `componentsById` directly makes the reactivity intentional, and means the
		// hook can now safely be memoised. See PROJECT_JUTE_PREVIEW.md.
	}, [transitionCategory, sceneOnLoadPlaySoundActions, sceneDispatch, sceneId, entityActionDispatch, sceneVideoIds, sceneModelIdleAnimationData, componentsById]);

	// Setting scene opacity transition for fade group
	useLayoutEffect(() => {
		if (transitionCategory === ITransCatTypes.hidden) return;
		const { transitions } = scene;
		if (!transitions?.[enterOrExit] && transitionCategory === ITransCatTypes.exit) return;
		let { type: effect, delay = 0, duration = 0 } = (transitions?.[enterOrExit] as ITransitionData) || {};
		// extend the scene fade transition duration to that of the longest child transition

		// TODO: check if correct
		if (transitions?.[enterOrExit]) {
			for (let i = 0; i < contentIds.length; i++) {
				const childId = contentIds[i];
				const transitionData = (componentsById?.[childId] as ISpatialComponentUnion).transitions?.[enterOrExit];
				if (!transitionData) continue;

				if (duration + delay < transitionData.duration + transitionData.delay) {
					// Extend duration to fit childs transition duration + delay
					duration = transitionData.duration + transitionData.delay - delay;
				}
			}
		}

		let { to, from } = getOpacityTransitionRange(transitionCategory);

		//
		if (duration === 0) {
			from = 1;
			to = 1;
		}

		// when entering, don't set scene transition to 0 when starting transiton with delay
		// to avoid unnecessary flashing.
		if (enterOrExit === ITransitionCategoryTypes.enter && duration !== 0 && effect !== ITransitionEffectTypes.fadeIn) {
			duration = duration + delay;
			delay = 0;
		}

		const { apply, navigated, restart } = syncTransitionConfig(
			lastSceneOpacityConfig,
			sceneOpacityTransition,
			transitionCategory,
			transitionSignature(sceneConfigSignature, from, to, duration, delay)
		);
		if (!apply) return;

		sceneOpacityTransition.set({ from, to, duration, delay });
		if (navigated || restart) sceneOpacityTransition.start();
	}, [enterOrExit, sceneOpacityTransition, transitionCategory, scene, sceneId, componentsById, contentIds, sceneConfigSignature]);

	// Setting scene spatial transition
	useLayoutEffect(() => {
		if (transitionCategory === ITransCatTypes.hidden) return;
		// set up transition for new run
		const fadeGroup = fadeGroupRef.current;
		const { transitions } = scene;
		const effectFallback = enterOrExit === ITransitionCategoryTypes.exit ? ITransitionEffectTypes.fadeOut : ITransitionEffectTypes.fadeIn;
		let { type: effect = effectFallback, delay = 0, duration } = (transitions?.[enterOrExit] as ITransitionData) || {};
		const originalDuration = duration;
		duration = duration || 100; // 100ms required to ensure that pointer events does not affect children of both scenes

		const { x, y, z } = fadeGroupRef.current?.position as Vector3;
		const [sx, sy, sz] = transitionCategory === ITransCatTypes.exit ? [x, y, z] : [0, 0, 0];

		let { to, from, threeProperty } = getSpatialTransitionRange({
			startPosition: [sx, sy, sz],
			enterEndPosition: [0, 0, 0],
			effect,
			category: transitionCategory,
			positionOffset: 2,
			sceneIsFlatImageTracked: parentSceneIsFlatImageTracked
		});

		if (!transitions?.[enterOrExit]?.duration) {
			switch (threeProperty) {
				case 'position':
					to = [0, 0, 0];
					from = [0, 0, 0];
					break;
				case 'scale':
					to = [1, 1, 1];
					from = [1, 1, 1];
			}
		}

		// TODO: Add opacity transition to transition manager to make sure opacity has finished before transiion manager
		// calls onfinish all and hides scene. For now the spatial transition is extended as a quick fix.
		if (effect === ITransitionEffectTypes.fadeOut) {
			for (let i = 0; i < contentIds.length; i++) {
				const childId = contentIds[i];
				const transitionData = (componentsById?.[childId] as ISpatialComponentUnion).transitions?.[enterOrExit];
				if (!transitionData) continue;
				if (duration + delay < transitionData.duration + transitionData.delay) {
					// Extend duration to fit childs transition duration + delay
					duration = transitionData.duration + transitionData.delay - delay;
				}
			}
		}

		const { apply, navigated, restart } = syncTransitionConfig(
			lastSceneSpatialConfig,
			sceneSpatialTransition,
			transitionCategory,
			transitionSignature(sceneConfigSignature, from, to, duration, delay, effect, threeProperty)
		);
		// This effect owns `startAll()`, so before the signature check every live edit — a colour, a label,
		// anything — replayed the scene enter transition for the whole scene. That was the long-standing
		// "scene enter-transition replay on every update" bug.
		if (!apply) return;

		// if the scene had a position or scale changing transition on exit and no position or scale changing
		// transition respectively on enter reset position/scale to the right values before
		if (transitionCategory === ITransCatTypes.enter) {
			switch (threeProperty) {
				case 'scale':
					fadeGroupRef.current?.position?.set?.(0, 0, 0);
					break;
				default:
					fadeGroupRef.current?.scale?.set?.(1, 1, 1);
					fadeGroupRef.current?.position?.set?.(0, 0, 0);
					break;
			}
		}

		sceneSpatialTransition.set({ from, to, duration, delay, easing: IEasingTypes.linear });

		// mutate threejs group
		sceneSpatialTransition.onChange((value) => {
			if (threeProperty === 'none') return;
			fadeGroupRef.current?.[threeProperty]?.set?.(...value);
			// const originGroup = threeScene.getObjectByName('origin-group');
			// originGroup?.[threeProperty]?.set?.(...value);
		});
		// A navigation begins a run. A content edit that lands on a running scene transition restarts the
		// group WITHOUT re-announcing it: `onStartAll` is where the scene hangs its enter-start side
		// effects, including the onLoad `playSound` actions, and an entity nudge must not replay those.
		if (navigated) transitionManager[sceneId].startAll();
		else if (restart) transitionManager[sceneId].restartAll();

		// Hide the scene group immediately when it should fade out and the original duration is undefined/0
		// and the duration hasn't been extended beyond the 100ms required so that pointer events don't affect 
		// both scenes (no child with longer transition).
		const hideContentImmediately = effect === ITransitionEffectTypes.fadeOut && (!originalDuration && duration === 100);
		if (fadeGroup) setTimeout(() => fadeGroup.visible = !hideContentImmediately, 0);

	}, [scene, sceneId, contentIds, enterOrExit, sceneDispatch, componentsById, transitionCategory, scene.transitions, sceneSpatialTransition, parentSceneIsFlatImageTracked, sceneConfigSignature]);

	return (
		<FadeGroup sceneId={sceneId} name={sceneId} ref={fadeGroupRef} scale={1} position={memoZeroTuple3 as any} transitionCategory={transitionCategory} transition={sceneOpacityTransition} sceneLevel visible={visible}>
			<ArComponents sceneId={sceneId} />
		</FadeGroup>
	);
};

Scene.displayName = 'Scene';
export default memo(Scene);
